import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { Button, Input } from "@/components/ui";
import { useAuthContext } from "@/contexts/AuthContext";
import { upsertAuthorProfile, type AuthorType, type PrimaryGoal } from "@/lib/firestore/authorProfiles";
import { consumePostAuthRoute } from "@/lib/authRedirect";
import { PressEyebrow } from "@/components/pullpress/PressVisuals";

const GOOGLE_SIGNUP_PROFILE_KEY = "pullpress_google_signup_profile";

const AUTHOR_TYPES: { value: AuthorType; label: string }[] = [
  { value: "first_book", label: "This is my first book." },
  { value: "memoirist", label: "I am writing a memoir." },
  { value: "business_author", label: "I am a business author." },
  { value: "imprint", label: "I publish as a team." },
];

const PRIMARY_GOALS: { value: PrimaryGoal; label: string }[] = [
  { value: "finish_manuscript", label: "I want to finish the book." },
  { value: "publish_print", label: "I need release files." },
  { value: "preserve_voice", label: "I want to preserve my voice." },
  { value: "build_catalog", label: "I want to build a catalog." },
];

type SignupStep = "account" | "profile";

export function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signUpWithEmail, signInWithGoogle, error, clearError } = useAuthContext();
  const packageIntent = normalizePackageIntent(searchParams.get("package"));
  const postAuthRoute = packageIntent ? `/new-project?package=${packageIntent}` : "/dashboard";
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [authorType, setAuthorType] = useState<AuthorType>("first_book");
  const [primaryGoal, setPrimaryGoal] = useState<PrimaryGoal>("finish_manuscript");
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [step, setStep] = useState<SignupStep>("account");
  const [completingGoogleRedirect, setCompletingGoogleRedirect] = useState(false);

  useEffect(() => {
    if (!user || completingGoogleRedirect) return;
    const pendingProfile = readPendingGoogleSignupProfile();
    if (!pendingProfile) {
      navigate(consumePostAuthRoute() ?? postAuthRoute, { replace: true });
      return;
    }

    setCompletingGoogleRedirect(true);
    clearError();
    setLocalError(null);
    void upsertAuthorProfile({
      user_id: user.uid,
      display_name: user.displayName ?? "",
      email: user.email ?? "",
      author_type: pendingProfile.authorType,
      primary_goal: pendingProfile.primaryGoal,
      package_intent: pendingProfile.packageIntent,
    }).then(() => {
      clearPendingGoogleSignupProfile();
      consumePostAuthRoute();
      navigate(pendingProfile.postAuthRoute, { replace: true });
    }).catch((err) => {
      setLocalError(err instanceof Error ? err.message : "Could not finish Google sign up.");
      setCompletingGoogleRedirect(false);
    });
  }, [clearError, completingGoogleRedirect, navigate, postAuthRoute, user]);

  const validateAccountStep = (): boolean => {
    clearError();
    const trimmedName = displayName.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) {
      setLocalError("Name is required.");
      return false;
    }
    if (!trimmedEmail) {
      setLocalError("Email address is required.");
      return false;
    }
    if (password.length < 6) {
      setLocalError("Password must be at least 6 characters.");
      return false;
    }
    if (password !== confirmPassword) {
      setLocalError("Passwords do not match.");
      return false;
    }
    setLocalError(null);
    return true;
  };

  const handleNextStep = () => {
    if (validateAccountStep()) setStep("profile");
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateAccountStep()) return;
    try {
      setSubmitting(true);
      const createdUser = await signUpWithEmail(email, password, displayName.trim());
      await upsertAuthorProfile({
        user_id: createdUser.uid,
        display_name: displayName.trim(),
        email: createdUser.email ?? email.trim(),
        author_type: authorType,
        primary_goal: primaryGoal,
        package_intent: packageIntent ?? "",
      });
      navigate(postAuthRoute);
    } catch {
      /* Auth context owns the rendered error state. */
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    clearError();
    setLocalError(null);
    setGoogleSubmitting(true);
    try {
      writePendingGoogleSignupProfile({
        authorType,
        primaryGoal,
        packageIntent: packageIntent ?? "",
        postAuthRoute,
      });
      const signedInUser = await signInWithGoogle(postAuthRoute);
      if (!signedInUser) return;
      await upsertAuthorProfile({
        user_id: signedInUser.uid,
        display_name: signedInUser.displayName ?? "",
        email: signedInUser.email ?? "",
        author_type: authorType,
        primary_goal: primaryGoal,
        package_intent: packageIntent ?? "",
      });
      clearPendingGoogleSignupProfile();
      consumePostAuthRoute();
      navigate(postAuthRoute);
    } catch {
      /* Auth context owns the rendered error state. */
    } finally {
      setGoogleSubmitting(false);
    }
  };

  const displayError = localError || error;
  const stepIndex = step === "account" ? 1 : 2;
  const stepCopy = step === "account"
    ? {
        kicker: "Create the account.",
        title: "Start with the essentials.",
        body: "Add your name, email, and password. The book details come next.",
      }
    : {
        kicker: "Set the direction.",
        title: "Tell us what kind of book this is.",
        body: "Choose the path that fits the first run.",
      };

  return (
    <AppShell variant="minimal">
      <div className="relative min-h-[calc(100dvh-64px)] overflow-hidden px-4 py-8 sm:px-6 lg:px-8">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-10 mx-auto h-[520px] max-w-[920px] rounded-full bg-[radial-gradient(circle,var(--as-paper-raised)_0%,var(--as-walnut-tint)_36%,transparent_68%)] opacity-70 blur-3xl"
        />
        <div className="relative mx-auto grid min-h-[calc(100dvh-128px)] w-full min-w-0 max-w-[1040px] gap-8 lg:grid-cols-[minmax(0,0.78fr)_minmax(360px,420px)] lg:items-center">
          <aside className="hidden lg:block">
            <PressEyebrow>PullPress</PressEyebrow>
            <h1 className="mt-5 max-w-[520px] font-display text-[clamp(3.1rem,5vw,5.6rem)] font-semibold leading-[0.94] tracking-as-normal text-ink-1">
              Start with the book.
            </h1>
            <p className="mt-6 max-w-[420px] font-text text-lg leading-as-read text-ink-2">
              Create the account. Then bring in the source.
            </p>
            <div className="mt-10 max-w-[420px] space-y-5">
              <FlowLine
                active={step === "account"}
                complete={step === "profile"}
                title="Create the account."
                body="Use your name, email, and password."
              />
              <FlowLine
                active={step === "profile"}
                complete={false}
                title="Set the book direction."
                body="Pick the path that fits the first run."
              />
            </div>
          </aside>

          <section className="w-[min(360px,calc(100vw-32px))] min-w-0 justify-self-start overflow-hidden rounded-[32px] border border-[color:color-mix(in_oklab,var(--as-rule)_50%,transparent)] bg-paper-raised/78 p-5 shadow-as-2 backdrop-blur-2xl sm:w-[min(420px,calc(100vw-48px))] sm:justify-self-center sm:p-7 lg:w-full lg:justify-self-stretch">
            <div className="flex items-center justify-between gap-4">
              <p className="font-ui text-sm font-semibold text-ink-3">{stepCopy.kicker}</p>
              <p className="hidden shrink-0 font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4 sm:block">Step {stepIndex} of 2</p>
            </div>
            <h2 className="mt-3 font-display text-[clamp(1.9rem,4vw,2.75rem)] font-semibold leading-tight tracking-as-normal text-ink-1">
              {stepCopy.title}
            </h2>
            <p className="mt-2 max-w-[360px] font-text text-base leading-as-read text-ink-2">
              {stepCopy.body}
            </p>
            <StepProgress step={step} />

            {displayError && (
              <div className="mt-5 rounded-as-2 border border-flag/30 bg-flag-tint p-3 font-ui text-sm leading-as-read text-flag">
                {displayError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 min-w-0 space-y-4">
              {step === "account" ? (
                <>
                  <Input label="Your name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Jane Smith" autoComplete="name" required className="h-12 text-base" />
                  <Input label="Email address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@domain.com" autoComplete="email" required className="h-12 text-base" />
                  <div className="grid gap-3">
                    <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6+ characters" autoComplete="new-password" required minLength={6} className="h-12 text-base" />
                    <Input label="Confirm password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat password" autoComplete="new-password" required minLength={6} className="h-12 text-base" />
                  </div>
                  <Button type="button" onClick={handleNextStep} variant="walnut" size="lg" className="mt-2 w-full">
                    Continue
                  </Button>
                </>
              ) : (
                <>
                  <SelectionGroup label="Author type">
                    {AUTHOR_TYPES.map((item) => (
                      <SelectionButton key={item.value} active={authorType === item.value} onClick={() => setAuthorType(item.value)}>
                        {item.label}
                      </SelectionButton>
                    ))}
                  </SelectionGroup>

                  <SelectionGroup label="Primary goal">
                    {PRIMARY_GOALS.map((item) => (
                      <SelectionButton key={item.value} active={primaryGoal === item.value} onClick={() => setPrimaryGoal(item.value)}>
                        {item.label}
                      </SelectionButton>
                    ))}
                  </SelectionGroup>

                  <div className="grid gap-2 sm:grid-cols-[0.42fr_1fr]">
                    <Button type="button" variant="secondary" disabled={submitting} onClick={() => setStep("account")}>
                      Back
                    </Button>
                    <Button type="submit" variant="walnut" disabled={submitting || completingGoogleRedirect} className="w-full">
                      {submitting || completingGoogleRedirect ? "Creating..." : "Create account"}
                    </Button>
                  </div>
                </>
              )}
            </form>

            <Button onClick={handleGoogle} variant="secondary" disabled={googleSubmitting || completingGoogleRedirect} className="mt-3 w-full gap-3">
              <span className="grid h-6 w-6 place-items-center rounded-full bg-paper font-ui text-sm font-semibold text-walnut-deep">G</span>
              {googleSubmitting || completingGoogleRedirect ? "Opening Google..." : "Create account with Google"}
            </Button>

            <div className="mt-5 text-center font-text text-sm text-ink-2">
              Already have an account?{" "}
              <Link to={packageIntent ? `/login?package=${packageIntent}` : "/login"} className="inline-flex min-h-10 items-center text-walnut-deep underline-offset-2 hover:underline">Sign in</Link>
            </div>
          </section>
        </div>
      </div>
    </AppShell>
  );
}

function normalizePackageIntent(value: string | null): string | null {
  if (value === "editorial" || value === "studio") return "editorial";
  if (value === "self-serve" || value === "creator") return "self-serve";
  if (value === "publishing" || value === "professional") return "publishing";
  if (value === "premium" || value === "signature") return "premium";
  if (value === "production" || value === "essentials") return "production";
  return null;
}

type PendingGoogleSignupProfile = {
  readonly authorType: AuthorType;
  readonly primaryGoal: PrimaryGoal;
  readonly packageIntent: string;
  readonly postAuthRoute: string;
};

function writePendingGoogleSignupProfile(profile: PendingGoogleSignupProfile): void {
  window.sessionStorage.setItem(GOOGLE_SIGNUP_PROFILE_KEY, JSON.stringify({
    author_type: profile.authorType,
    primary_goal: profile.primaryGoal,
    package_intent: profile.packageIntent,
    post_auth_route: profile.postAuthRoute,
  }));
}

function readPendingGoogleSignupProfile(): PendingGoogleSignupProfile | null {
  const raw = window.sessionStorage.getItem(GOOGLE_SIGNUP_PROFILE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    const authorType = parsed.author_type;
    const primaryGoal = parsed.primary_goal;
    const packageIntent = parsed.package_intent;
    const postAuthRoute = parsed.post_auth_route;
    if (!isAuthorType(authorType) || !isPrimaryGoal(primaryGoal)) return null;
    if (typeof packageIntent !== "string" || typeof postAuthRoute !== "string" || !postAuthRoute.startsWith("/")) return null;
    return { authorType, primaryGoal, packageIntent, postAuthRoute };
  } catch {
    return null;
  }
}

function clearPendingGoogleSignupProfile(): void {
  window.sessionStorage.removeItem(GOOGLE_SIGNUP_PROFILE_KEY);
}

function isAuthorType(value: unknown): value is AuthorType {
  return typeof value === "string" && AUTHOR_TYPES.some((item) => item.value === value);
}

function isPrimaryGoal(value: unknown): value is PrimaryGoal {
  return typeof value === "string" && PRIMARY_GOALS.some((item) => item.value === value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function StepProgress({ step }: { step: SignupStep }) {
  return (
    <div className="mt-5 grid grid-cols-2 gap-2" aria-label="Signup progress">
      <span className={`h-1 rounded-full ${step === "account" || step === "profile" ? "bg-walnut" : "bg-rule"}`} />
      <span className={`h-1 rounded-full ${step === "profile" ? "bg-walnut" : "bg-rule"}`} />
    </div>
  );
}

function FlowLine({ active, complete, title, body }: {
  active: boolean;
  complete: boolean;
  title: string;
  body: string;
}) {
  return (
    <div className="grid grid-cols-[18px_1fr] gap-4">
      <span className={`mt-1 h-3.5 w-3.5 rounded-full border ${complete ? "border-verify bg-verify" : active ? "border-walnut bg-walnut" : "border-rule-strong bg-paper-raised"}`} />
      <div>
        <p className={`font-ui text-sm font-semibold ${active || complete ? "text-ink-1" : "text-ink-3"}`}>{title}</p>
        <p className="mt-1 font-text text-sm leading-as-read text-ink-3">{body}</p>
      </div>
    </div>
  );
}

function SelectionGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <PressEyebrow>{label}</PressEyebrow>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function SelectionButton({ active, onClick, children }: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-12 rounded-as-2 border px-3 py-2 text-left font-ui text-sm font-semibold leading-as-ui transition-colors ${active ? "border-walnut bg-walnut-tint text-walnut-deep" : "border-rule-faint bg-paper/72 text-ink-2 hover:border-walnut-soft hover:text-walnut-deep"}`}
    >
      {children}
    </button>
  );
}
